import { Button, Flex, Typography, theme } from 'antd';
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';

import { UIUtil } from '@suntex.core/animation';
import { $Color } from '@suntex.core/color';

import {
  BADGE_COLORS,
  type BadgeIconOption,
  type BadgeIconSection,
  ICONS_PER_ROW,
  SKILL_BADGE_ICON,
  getBadgeIconSections,
  resolveIconForColorPick,
} from '../display/badgeOptions';
import { createPlayerBadge } from '../display/renderPlayerBadge';

import { BadgeCanvas } from './BadgeCanvas';
import { BadgeLibraryProvider } from './BadgeLibrary';

/** Radius of the badge circle in the Animate export. */
const BADGE_RADIUS = 80;
const ICON_SIZE = 56;
const SWATCH_SIZE = 32;
const SWATCHES_PER_ROW = 7;
const GRID_GAP = 6;
const LOCKED_OPACITY = 0.3;
/** Scale of `PlayerIcon` within the Badge symbol (`LevelIcon` is unscaled). */
const PLAYER_ICON_SCALE = 0.3172;

export interface PlayerBadgeSelectProps {
  /** Player's sticker count; determines which icons are unlocked. */
  stickers: number;
  /** Current badge icon (0 = auto skill badge). */
  icon: number;
  /** Current badge background color. */
  background: number;
  onSave: (icon: number, background: number) => void | Promise<void>;
}

/** Lets a player choose their avatar badge icon and background color. */
export function PlayerBadgeSelect(props: PlayerBadgeSelectProps) {
  return (
    <BadgeLibraryProvider>
      <PlayerBadgeSelectContent {...props} />
    </BadgeLibraryProvider>
  );
}

function PlayerBadgeSelectContent({
  stickers,
  icon: initialIcon,
  background: initialBackground,
  onSave,
}: PlayerBadgeSelectProps) {
  const [icon, setIcon] = useState(initialIcon);
  const [background, setBackground] = useState(initialBackground);
  const [saving, setSaving] = useState(false);
  const iconSections = useMemo(
    () => getBadgeIconSections(stickers),
    [stickers],
  );

  function pickColor(color: number) {
    setIcon((icon) => resolveIconForColorPick(icon, stickers));
    setBackground(color);
  }

  async function save() {
    setSaving(true);
    try {
      await onSave(icon, background);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Flex wrap gap="large" justify="center" style={{ padding: 16 }}>
      <Flex
        vertical
        align="center"
        gap="middle"
        style={{ width: SWATCHES_PER_ROW * (SWATCH_SIZE + GRID_GAP) }}
      >
        <Button
          type="primary"
          size="large"
          loading={saving}
          onClick={() => void save()}
        >
          Save this Player Avatar
        </Button>
        <BadgeCanvas
          width={190}
          height={200}
          deps={[stickers, icon, background]}
          draw={(lib) =>
            createPlayerBadge(lib, {
              stickers,
              icon,
              background,
              interactive: false,
            })
          }
        />
        <Button
          onClick={() => setIcon(SKILL_BADGE_ICON)}
          disabled={icon === SKILL_BADGE_ICON}
        >
          Use my skill badge
        </Button>
        <section style={{ alignSelf: 'stretch' }}>
          <Typography.Title level={4}>Choose a color</Typography.Title>
          <div style={gridStyle(SWATCH_SIZE)}>
            {BADGE_COLORS.map((color) => (
              <SelectableCircle
                key={color}
                size={SWATCH_SIZE}
                label={`Color ${hex(color)}`}
                color={hex(color)}
                selected={icon !== SKILL_BADGE_ICON && color === background}
                onSelect={() => pickColor(color)}
              />
            ))}
          </div>
        </section>
      </Flex>

      <Flex
        vertical
        gap="middle"
        style={{
          width: ICONS_PER_ROW * (ICON_SIZE + GRID_GAP),
          maxWidth: '100%',
        }}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Choose an icon
        </Typography.Title>
        {iconSections.map((section) => (
          <section key={section.options[0]!.value}>
            <Typography.Text strong>{sectionLabel(section)}</Typography.Text>
            <div style={gridStyle(ICON_SIZE)}>
              {section.options.map((option) => (
                <IconButton
                  key={option.value}
                  option={option}
                  selected={option.value === icon}
                  onSelect={() => setIcon(option.value)}
                />
              ))}
            </div>
          </section>
        ))}
      </Flex>
    </Flex>
  );
}

function IconButton({
  option,
  selected,
  onSelect,
}: {
  option: BadgeIconOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const { kind, frame, value, locked, unlockStickers } = option;

  return (
    <SelectableCircle
      size={ICON_SIZE}
      label={`${kind === 'level' ? 'Level' : 'Icon'} ${value}${locked ? ' (locked)' : ''}`}
      color={locked ? '#888888' : '#444444'}
      title={
        locked
          ? unlockStickers
            ? `Unlocks at ${formatNumber(unlockStickers)} stickers`
            : `Unlocks at skill level ${frame}`
          : undefined
      }
      selected={selected}
      disabled={locked}
      onSelect={onSelect}
    >
      <BadgeCanvas
        width={BADGE_RADIUS * 2}
        height={BADGE_RADIUS * 2}
        displayWidth={ICON_SIZE}
        deps={[kind, frame]}
        draw={(lib) => {
          const clip = new (
            kind === 'level' ? lib.LevelIcon : lib.PlayerIcon
          )!();
          UIUtil.setFrame(clip, frame);
          clip.x = clip.y = BADGE_RADIUS;
          if (kind === 'player') clip.scaleX = clip.scaleY = PLAYER_ICON_SCALE;
          return clip;
        }}
      />
    </SelectableCircle>
  );
}

function SelectableCircle({
  size,
  label,
  title,
  color,
  selected,
  disabled,
  onSelect,
  children,
}: {
  size: number;
  label: string;
  title?: string;
  color: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children?: ReactNode;
}) {
  const { token } = theme.useToken();

  return (
    <button
      type="button"
      aria-label={label}
      title={title}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      style={{
        width: size,
        height: size,
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        overflow: 'hidden',
        background: color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? LOCKED_OPACITY : 1,
        outline: selected ? `3px solid ${token.colorPrimary}` : undefined,
        outlineOffset: 2,
      }}
    >
      {children}
    </button>
  );
}

function sectionLabel({ minStickers, andUp }: BadgeIconSection) {
  return minStickers === null
    ? 'Skill level icons'
    : `Unlock at ${formatNumber(minStickers)}${andUp ? '+' : ''} stickers`;
}

function formatNumber(n: number) {
  return n.toLocaleString('en-US');
}

function gridStyle(cellSize: number): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, ${cellSize}px)`,
    gap: GRID_GAP,
  };
}

function hex(color: number) {
  return $Color.toHexString($Color.from(color));
}
